import React, { useCallback, useEffect, useState } from "react";
import "./Card.scss";
import { Row, Col, Input, Checkbox, Button, Select } from "antd";
import Cards from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";
import Validates from "@utils/Validates";
import { LeftOutlined } from "@ant-design/icons";
import PaymentMethod from "../PaymentMethod/PaymentMethod";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";
import Suggestion from "@components/Suggestion/Suggestion";
import { useUser } from "contexts/UserContext";
import { paymentApi } from "services/PaymentApi";
const { Option } = Select;

interface CardProps {
  isUpdating: boolean;
  setIsUpdating: (is: boolean) => void;
}

let debounceTimer: any;

const Card: React.FC<CardProps> = ({ isUpdating, setIsUpdating }) => {
  const [cardData, setCardData] = useState({
    number: "",
    expiry: "",
    cvc: "",
    name: "",
    focus: "" as "number" | "name" | "expiry" | "cvc",
    type: "",
    isDefault: false,
    address: {
      first_name: "",
      last_name: "",
      phone: "",
      street: "",
      apt_unit: "",
      city: "",
      province: "",
      postal_code: "",
    },
  });

  const [isSelectPayment, setIsSelectPayment] = useState(false);
  const { userProfile, userAddresses } = useUser();
  const [defaultAddress, setDefaultAddress] = useState(userProfile?.address_id);
  const [isOpenSuggestion, setIsOpenSuggestion] = useState(false);
  const [searchResult, setSearchResult] = useState<any[]>([]);

  const getUserAddress = useCallback(() => {
    if (!defaultAddress) return;
    const address = userAddresses.find(
      (item: any) => item.address_id === defaultAddress
    );
    return address;
  }, [defaultAddress]);

  useEffect(() => {
    setCardData((prevData) => ({
      ...prevData,
      address: {
        first_name: userProfile?.first_name || "",
        last_name: userProfile?.last_name || "",
        phone: userProfile?.phone || "",
        street: getUserAddress()?.street || "",
        apt_unit: getUserAddress()?.apartment || "",
        city: getUserAddress()?.city || "",
        province: getUserAddress()?.province || "",
        postal_code: getUserAddress()?.postal_code || "",
      },
    }));
  }, [defaultAddress, userProfile]);

  const handleCardChange = (value: any, type: string) => {
    if (type === "number" || type === "cvc" || type === "expiry") {
      value = Validates.validateNumberForm(value);
      if (type === "expiry" && value.length > 2) {
        value = value.slice(0, 2) + "/" + value.slice(2, 4);
        if (value.slice(0, 2) > 12) {
          value = "12" + value.slice(2, 5);
        }
      }
      if (type === "cvc" && value.length > 3) {
        return;
      }
    }
    if (type === "name") {
      value = Validates.validateTextForm(value);
    }
    setCardData({ ...cardData, [type]: value });
  };

  const handleAddCard = async () => {
    setIsUpdating(true);

    const body = {
      card_number: cardData.number,
      cardholder_name: cardData.name,
      expiration_date: cardData.expiry,
      payment_type: "credit card",
      card_type: cardData.type,
      is_default: cardData.isDefault,
      cvv: parseInt(cardData.cvc),
      address: {
        first_name: cardData.address.first_name,
        last_name: cardData.address.last_name,
        phone: cardData.address.phone,
        street: cardData.address.street,
        apt_unit: cardData.address.apt_unit,
        city: cardData.address.city,
        province: cardData.address.province,
        postal_code: cardData.address.postal_code,
      },
    };

    if (
      cardData.number === "" ||
      cardData.name === "" ||
      cardData.expiry === "" ||
      cardData.cvc === ""
    ) {
      setIsUpdating(false);
      Helpers.notification.warning("Please fill in all card information");
      return;
    }

    if (
      cardData.address.first_name === "" ||
      cardData.address.last_name === "" ||
      cardData.address.street === "" ||
      cardData.address.apt_unit == null ||
      cardData.address.city === "" ||
      cardData.address.province === "" ||
      cardData.address.postal_code === ""
    ) {
      setIsUpdating(false);
      Helpers.notification.warning("Please fill in all address information");
      return;
    }

    try {
      const response = await paymentApi.createPayment(body);
      if (response.status === 201) {
        setIsUpdating(false);
        setIsSelectPayment(!isSelectPayment);
        Helpers.showSuccessUpdated("Card added successfully");
      }
    } catch (error: any) {
      setIsUpdating(false);
      Helpers.notification.error("Cannot add card. Please try again");
      errorLog("Card.tsx - handleAddCard", error);
    }
  };

  return (
    <>
      {isSelectPayment ? (
        <PaymentMethod isUpdating={isUpdating} setIsUpdating={setIsUpdating} />
      ) : (
        <div className="Card">
          <div className="section">
            <div className="section-title">
              <LeftOutlined
                style={{ fontSize: "12px" }}
                onClick={() => setIsSelectPayment(!isSelectPayment)}
              />
              Credit/Debit Card
            </div>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div className="form-group">
                  <div className="title">
                    Card Number <span className="text-danger">*</span>
                  </div>
                  <Input
                    size="large"
                    variant="filled"
                    maxLength={16}
                    value={cardData.number}
                    onChange={(e) => handleCardChange(e.target.value, "number")}
                    onFocus={(e) =>
                      setCardData({ ...cardData, focus: "number" })
                    }
                  />
                </div>
                <div className="form-group">
                  <div className="title">
                    Cardholder's Name <span className="text-danger">*</span>
                  </div>
                  <Input
                    size="large"
                    variant="filled"
                    value={cardData.name}
                    onChange={(e) => handleCardChange(e.target.value, "name")}
                    onFocus={(e) => setCardData({ ...cardData, focus: "name" })}
                  />
                </div>
                <Row gutter={12}>
                  <Col span={12}>
                    <div className="form-group">
                      <div className="title">
                        Expiry <span className="text-danger">*</span>
                      </div>
                      <Input
                        size="large"
                        variant="filled"
                        value={cardData.expiry}
                        maxLength={5}
                        placeholder="MM/YY"
                        onChange={(e) =>
                          handleCardChange(e.target.value, "expiry")
                        }
                        onFocus={(e) =>
                          setCardData({ ...cardData, focus: "expiry" })
                        }
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="form-group">
                      <div className="title">
                        CVV <span className="text-danger">*</span>
                      </div>
                      <Input.Password
                        size="large"
                        variant="filled"
                        value={cardData.cvc}
                        onChange={(e) =>
                          handleCardChange(e.target.value, "cvc")
                        }
                        onFocus={(e) =>
                          setCardData({ ...cardData, focus: "cvc" })
                        }
                      />
                    </div>
                  </Col>
                </Row>
                <Checkbox
                  onChange={() =>
                    setCardData({ ...cardData, isDefault: !cardData.isDefault })
                  }
                >
                  Set as default method
                </Checkbox>
              </Col>
              <Col span={12}>
                <div className="card-preview">
                  <Cards
                    number={cardData.number}
                    expiry={cardData.expiry}
                    cvc={cardData.cvc || ""}
                    name={cardData.name}
                    focused={cardData.focus}
                    callback={(type: any) =>
                      setCardData({ ...cardData, type: type.issuer })
                    }
                  />
                </div>
              </Col>
            </Row>
            <div className="billing-address">
              <div className="section-title">Billing Address</div>
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <div className="form-group">
                    <div className="title">
                      First name <span className="text-danger">*</span>
                    </div>
                    <Input
                      variant="filled"
                      size="large"
                      value={cardData.address.first_name}
                      onChange={(e) =>
                        setCardData({
                          ...cardData,
                          address: {
                            ...cardData.address,
                            first_name: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="form-group">
                    <div className="title">
                      Last name <span className="text-danger">*</span>
                    </div>
                    <Input
                      variant="filled"
                      size="large"
                      value={cardData.address.last_name}
                      onChange={(e) =>
                        setCardData({
                          ...cardData,
                          address: {
                            ...cardData.address,
                            last_name: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </Col>
              </Row>
              <div className="form-group">
                <div className="title">Phone</div>
                <Input
                  variant="filled"
                  size="large"
                  value={cardData.address.phone}
                  onChange={(e) =>
                    setCardData({
                      ...cardData,
                      address: { ...cardData.address, phone: e.target.value },
                    })
                  }
                />
              </div>
              <br />
              <div className="form-group">
                <Select
                  className="w-100"
                  size="large"
                  value={defaultAddress}
                  onChange={(value) => setDefaultAddress(value)}
                >
                  {userAddresses.map((address: any) => (
                    <Option key={address.address_id} value={address.address_id}>
                      {address.label}
                    </Option>
                  ))}
                </Select>
              </div>
              <br />
              <div className="form-group map-search-input">
                <div className="title">
                  Street Address<span className="text-danger">*</span>
                </div>
                <Input
                  size="large"
                  variant="filled"
                  value={cardData.address.street}
                  onChange={(e) => {
                    const query = e.target.value;
                    setIsOpenSuggestion(true);
                    if (query == "") {
                      setIsOpenSuggestion(false);
                    }
                    clearTimeout(debounceTimer);
                    debounceTimer = setTimeout(async () => {
                      const result = await Helpers.handleSearchLocation(query);
                      setSearchResult(result);
                    }, 300);
                    setCardData({
                      ...cardData,
                      address: { ...cardData.address, street: query },
                    });
                  }}
                />
                {isOpenSuggestion && (
                  <Suggestion searchResult={searchResult} onSelect={() => {}} />
                )}
              </div>
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <div className="form-group">
                    <div className="title">Apt, Suite, etc</div>
                    <Input
                      size="large"
                      variant="filled"
                      value={cardData.address.apt_unit}
                      onChange={(e) =>
                        setCardData({
                          ...cardData,
                          address: {
                            ...cardData.address,
                            apt_unit: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="form-group">
                    <div className="title">
                      City/Town/County <span className="text-danger">*</span>
                    </div>
                    <Input
                      size="large"
                      variant="filled"
                      value={cardData.address.city}
                      onChange={(e) => {
                        setCardData({
                          ...cardData,
                          address: {
                            ...cardData.address,
                            city: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="form-group">
                    <div className="title">
                      Province<span className="text-danger">*</span>
                    </div>
                    <Input
                      size="large"
                      variant="filled"
                      value={cardData.address.province}
                      onChange={(e) =>
                        setCardData({
                          ...cardData,
                          address: {
                            ...cardData.address,
                            province: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="form-group">
                    <div className="title">
                      Postal/Zip Code<span className="text-danger">*</span>
                    </div>
                    <Input
                      size="large"
                      variant="filled"
                      value={cardData.address.postal_code}
                      onChange={(e) =>
                        setCardData({
                          ...cardData,
                          address: {
                            ...cardData.address,
                            postal_code: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </Col>
              </Row>
            </div>
            <div className="form-submit">
              <Button
                className="cancel-btn"
                onClick={() => setIsSelectPayment(true)}
              >
                Cancel
              </Button>

              <Button className="save-card-btn" onClick={handleAddCard}>
                Add
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Card;
